import React, { memo } from "react";

import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";

import { countTires, formatTireDimension, TIRE_SET_INTRO_SAMPLE } from "../consts";
import { TireSetSceneProps } from "../types";

/** Korak "Skladište": skladište seta s adresom, set na polici i praćenje lokacije. */
const TireSetWarehouseScene: React.FC<TireSetSceneProps> = ({ formValues }) => {
  const warehouse = formValues.warehouse;
  const warehouseName = warehouse?.name || TIRE_SET_INTRO_SAMPLE.warehouseName;
  const warehouseAddress = warehouse
    ? [warehouse.addressLine1, warehouse.addressCity].filter(Boolean).join(", ")
    : TIRE_SET_INTRO_SAMPLE.warehouseAddress;
  const firstTire = formValues.tires[0];
  const dimension = formatTireDimension(firstTire) || TIRE_SET_INTRO_SAMPLE.dimension;
  const quantity = countTires(formValues.tires) || TIRE_SET_INTRO_SAMPLE.quantity;

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-11 h-11 rounded-md bg-blue-50 flex-shrink-0">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 21V8l9-5 9 5v13M7 21v-8h10v8M7 17h10" />
              </svg>
            </span>
            <div className="min-w-0">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Skladište</div>
              <div className="text-sm font-bold text-slate-800 truncate">{warehouseName}</div>
              <div className="text-[0.65rem] text-slate-400 truncate">{warehouseAddress || "Adresa nije upisana"}</div>
            </div>
          </div>

          {/* Police skladišta: set guma na svojem mjestu. */}
          <div className="mt-4 space-y-2">
            {["A", "B", "C"].map((shelf) => (
              <div key={shelf} className="flex items-center gap-2">
                <span className="w-5 text-[0.6rem] font-semibold text-slate-400">{shelf}</span>
                <div className="flex flex-1 gap-1.5">
                  {[0, 1, 2, 3].map((slot) => {
                    const isThisSet = shelf === "B" && slot === 1;
                    return (
                      <span
                        key={slot}
                        className={
                          isThisSet
                            ? "flex-1 h-6 rounded-sm bg-amber-400 ring-2 ring-amber-200"
                            : "flex-1 h-6 rounded-sm bg-slate-100"
                        }
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Set guma</div>
          <div className="mt-2 text-xs font-bold text-slate-800">{formValues.code || TIRE_SET_INTRO_SAMPLE.code}</div>
          <div className="text-[0.65rem] text-slate-400">
            {dimension} · {quantity} kom
          </div>
          <div className="mt-3 inline-flex px-2 py-0.5 rounded-full text-[0.65rem] font-semibold bg-emerald-100 text-emerald-700">
            Uskladišteno
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Uvijek znate gdje su gume"
          subtitle="Lokacija seta se prati automatski"
          iconClassName="bg-emerald-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 21s7-7.75 7-13a7 7 0 00-14 0c0 5.25 7 13 7 13z" />
              <circle cx="12" cy="8" r="2.5" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(TireSetWarehouseScene);
